/**
 * High-intelligence agent interface layer.
 * Provides semantic intent parsing, plan decomposition, working memory,
 * composable pipelines, and function-calling protocol for orchestration agents.
 */

// --- Working Memory (key-value context store) ---

export interface MemorySlot {
  key: string;
  value: unknown;
  type: 'string' | 'number' | 'json' | 'ref';
  set_at: number;
  ttl_ms: number | null;
  source: string;
}

const memory: Map<string, MemorySlot> = new Map();

export function memSet(key: string, value: unknown, opts?: { type?: MemorySlot['type']; ttl_ms?: number; source?: string }) {
  memory.set(key, {
    key,
    value,
    type: opts?.type ?? (typeof value === 'number' ? 'number' : typeof value === 'string' ? 'string' : 'json'),
    set_at: Date.now(),
    ttl_ms: opts?.ttl_ms ?? null,
    source: opts?.source ?? 'agent',
  });
}

export function memGet(key: string): unknown | undefined {
  const slot = memory.get(key);
  if (!slot) return undefined;
  if (slot.ttl_ms && Date.now() - slot.set_at > slot.ttl_ms) {
    memory.delete(key);
    return undefined;
  }
  return slot.value;
}

export function memDump(): MemorySlot[] {
  const now = Date.now();
  Array.from(memory.entries()).forEach(([k, v]) => {
    if (v.ttl_ms && now - v.set_at > v.ttl_ms) memory.delete(k);
  });
  return Array.from(memory.values());
}

export function memClear() { memory.clear(); }

// --- Semantic Intent Parsing ---

export interface ParsedIntent {
  raw: string;
  intent: string;
  confidence: number;
  params: Record<string, string | number | boolean>;
  flags: string[];
  pipe_to?: string;
  is_compound: boolean;
  sub_commands?: string[];
}

const INTENT_PATTERNS: { pattern: RegExp; intent: string; extract: (m: RegExpMatchArray) => Record<string, string | number | boolean> }[] = [
  { pattern: /^(?:show|get|what(?:'s| is))\s+(?:the\s+)?status/i, intent: 'status', extract: () => ({}) },
  { pattern: /^(?:find|search|filter|show)\s+(?:all\s+)?(?:tracks?\s+)?(?:that are |in |with status )?(draft|review|in_review|approved|published)/i, intent: 'select', extract: m => ({ filter: m[1].replace('review', 'in_review') }) },
  { pattern: /^(?:select|target|pick|grab)\s+(.+)/i, intent: 'select', extract: m => ({ filter: m[1] }) },
  { pattern: /^(?:approve|lgtm|ship it|looks good)/i, intent: 'approve', extract: () => ({}) },
  { pattern: /^(?:publish|release|push live|go live)/i, intent: 'publish', extract: () => ({}) },
  { pattern: /^(?:delete|remove|nuke|trash|drop)\s*/i, intent: 'delete', extract: () => ({}) },
  { pattern: /^(?:tag|label|mark)\s+(?:as |with )?(.+)/i, intent: 'tag', extract: m => ({ tag: m[1] }) },
  { pattern: /^(?:align|sync|lyric.?sync)/i, intent: 'align', extract: () => ({}) },
  { pattern: /^(?:separate|stems?|demucs|split.?stems?|stem.?separate)/i, intent: 'separate', extract: () => ({}) },
  { pattern: /^(?:export|download|dump)\s*(?:as )?(csv|jsonl|json)?/i, intent: 'export', extract: m => ({ format: m[1] || 'csv' }) },
  { pattern: /^(?:review|send.?back|reject|needs.?work)/i, intent: 'review', extract: () => ({}) },
  { pattern: /^(?:open|play|view|inspect)\s*(.+)?/i, intent: 'open', extract: m => ({ target: m[1]?.trim() || '' }) },
  { pattern: /^(?:discover|connectors?|integrations?|apis?)/i, intent: 'discover', extract: () => ({}) },
  { pattern: /^(?:describe|info|about|explain)\s+(.+)/i, intent: 'describe', extract: m => ({ target: m[1] }) },
  { pattern: /^(?:connect|link|wire|integrate)\s+(.+)/i, intent: 'connect', extract: m => ({ target: m[1] }) },
  { pattern: /^(?:queue|tasks?|jobs?|async)/i, intent: 'queue', extract: () => ({}) },
  { pattern: /^(?:webhooks?)\s*(.*)/i, intent: 'webhooks', extract: m => ({ id: m[1]?.trim() || '' }) },
  { pattern: /^(?:manifest|capabilities|spec)\s*(--json)?/i, intent: 'manifest', extract: m => ({ json: !!m[1] }) },
  { pattern: /^(?:status|state)\s*(--json)?/i, intent: 'status', extract: m => ({ json: !!m[1] }) },
  { pattern: /^(?:layer|level|mode)\s*(basic|advanced|expert)?/i, intent: 'layer', extract: m => ({ level: m[1] || '' }) },
  { pattern: /^(?:verbose|verbosity|detail)\s*(minimal|standard|verbose)?/i, intent: 'verbosity', extract: m => ({ level: m[1] || '' }) },
  { pattern: /^(?:help|\?|commands|what can)/i, intent: 'help', extract: () => ({}) },
  { pattern: /^(?:context|ctx|where am i)/i, intent: 'context', extract: () => ({}) },
  { pattern: /^(?:model|transformer|roformer|picker)\s*(.*)/i, intent: 'model', extract: m => ({ filter: m[1]?.trim() || '', model: m[1]?.trim() || '' }) },
  { pattern: /^(?:plan|decompose|break.?down)\s+(.+)/i, intent: 'plan', extract: m => ({ goal: m[1] }) },
  { pattern: /^(?:think|reason|analyze|consider)\s+(.+)/i, intent: 'think', extract: m => ({ about: m[1] }) },
  { pattern: /^(?:remember|memo|store|set)\s+(\w+)\s*=\s*(.+)/i, intent: 'mem_set', extract: m => ({ key: m[1], value: m[2] }) },
  { pattern: /^(?:recall|get|what is|what's)\s+\$(\w+)/i, intent: 'mem_get', extract: m => ({ key: m[1] }) },
  { pattern: /^(?:memory|mem|store|vars)/i, intent: 'mem_dump', extract: () => ({}) },
  { pattern: /^(?:clear memory|forget all|reset mem)/i, intent: 'mem_clear', extract: () => ({}) },
  { pattern: /^(?:introspect|self|reflect|meta)/i, intent: 'introspect', extract: () => ({}) },
  // Expert-level workflow intents
  { pattern: /^(?:audit|log|history|provenance)\s*(.*)/i, intent: 'audit', extract: m => ({ scope: m[1]?.trim() || 'all' }) },
  { pattern: /^(?:evaluate|eval|score|grade|assess)\s+(.+)/i, intent: 'evaluate', extract: m => ({ criteria: m[1] }) },
  { pattern: /^(?:hypothesis|hyp|test.?if|assume)\s+(.+)/i, intent: 'hypothesis', extract: m => ({ statement: m[1] }) },
  { pattern: /^(?:batch|orchestrate|bulk)\s+(.+)/i, intent: 'batch', extract: m => ({ operations: m[1] }) },
  { pattern: /^(?:schedule|cron|temporal|at|every)\s+(.+)/i, intent: 'schedule', extract: m => ({ spec: m[1] }) },
  { pattern: /^(?:dag|graph|dependency|depends)\s*(.*)/i, intent: 'dag', extract: m => ({ expr: m[1]?.trim() || '' }) },
  { pattern: /^(?:trace|lineage|provenance.?of)\s+(.+)/i, intent: 'trace', extract: m => ({ target: m[1] }) },
  { pattern: /^(?:policy|policies|rls|permissions?|access)/i, intent: 'policy', extract: () => ({}) },
  { pattern: /^(?:stream|events?|subscribe|listen)\s*(.*)/i, intent: 'stream', extract: m => ({ channel: m[1]?.trim() || 'all' }) },
  { pattern: /^(?:cot|chain.?of.?thought|reasoning.?trace|show.?work)/i, intent: 'cot', extract: () => ({}) },
  { pattern: /^(?:delegate|dispatch|assign)\s+(.+?)\s+(?:to)\s+(.+)/i, intent: 'delegate', extract: m => ({ task: m[1], agent: m[2] }) },
  { pattern: /^(?:fork|spawn|parallel)\s+(.+)/i, intent: 'fork', extract: m => ({ tasks: m[1] }) },
  { pattern: /^(?:join|await|barrier|sync)\s*(.*)/i, intent: 'join', extract: m => ({ ids: m[1]?.trim() || '' }) },
  { pattern: /^(?:checkpoint|snapshot|save.?state)/i, intent: 'checkpoint', extract: () => ({}) },
  { pattern: /^(?:rollback|revert|undo)\s*(.*)/i, intent: 'rollback', extract: m => ({ to: m[1]?.trim() || 'last' }) },
  { pattern: /^(?:compare|diff|delta)\s+(.+?)\s+(?:vs|with|against)\s+(.+)/i, intent: 'compare', extract: m => ({ a: m[1], b: m[2] }) },
  { pattern: /^(?:separate|split|demucs|stems?|extract stems?)\s*(.*)/i, intent: 'stems_separate', extract: m => ({ target: m[1]?.trim() || '' }) },
];

export function parseIntent(raw: string): ParsedIntent {
  const flags: string[] = [];
  let cleaned = raw.trim();

  // Extract flags (--flag)
  const flagMatch = cleaned.matchAll(/--(\w+)/g);
  for (const m of flagMatch) flags.push(m[1]);
  cleaned = cleaned.replace(/--\w+/g, '').trim();

  // Detect pipe operator
  let pipe_to: string | undefined;
  if (cleaned.includes(' | ')) {
    const parts = cleaned.split(' | ');
    cleaned = parts[0].trim();
    pipe_to = parts.slice(1).join(' | ').trim();
  }

  // Detect compound commands (semicolons or "then")
  const is_compound = /[;]|\bthen\b/.test(cleaned);
  const sub_commands = is_compound
    ? cleaned.split(/[;]|\bthen\b/).map(s => s.trim()).filter(Boolean)
    : undefined;

  if (is_compound && sub_commands) {
    cleaned = sub_commands[0];
  }

  // Match against intent patterns
  for (const { pattern, intent, extract } of INTENT_PATTERNS) {
    const m = cleaned.match(pattern);
    if (m) {
      return {
        raw,
        intent,
        confidence: 0.95,
        params: extract(m),
        flags,
        pipe_to,
        is_compound,
        sub_commands,
      };
    }
  }

  // Fuzzy matching: find closest command by Levenshtein-like scoring
  const words = cleaned.toLowerCase().split(/\s+/);
  const candidates = ['status', 'select', 'approve', 'publish', 'delete', 'tag', 'export', 'review', 'open', 'discover', 'describe', 'connect', 'queue', 'webhooks', 'manifest', 'layer', 'help', 'plan', 'memory', 'introspect', 'model'];
  let bestMatch = '';
  let bestScore = 0;

  for (const cmd of candidates) {
    for (const word of words) {
      const score = similarity(word, cmd);
      if (score > bestScore) {
        bestScore = score;
        bestMatch = cmd;
      }
    }
  }

  if (bestScore >= 0.7) {
    return {
      raw,
      intent: bestMatch,
      confidence: bestScore,
      params: { inferred: true, from: cleaned },
      flags,
      pipe_to,
      is_compound,
      sub_commands,
    };
  }

  return {
    raw,
    intent: 'unknown',
    confidence: 0,
    params: { input: cleaned },
    flags,
    pipe_to,
    is_compound,
    sub_commands,
  };
}

function similarity(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const longer = a.length > b.length ? a : b;
  const shorter = a.length > b.length ? b : a;
  if (longer.startsWith(shorter) || longer.endsWith(shorter)) return shorter.length / longer.length + 0.2;
  let matches = 0;
  for (let i = 0; i < shorter.length; i++) {
    if (longer.includes(shorter[i])) matches++;
  }
  return matches / longer.length;
}

// --- Plan Decomposition ---

export interface PlanStep {
  id: string;
  action: string;
  params: Record<string, unknown>;
  depends_on: string[];
  state: 'pending' | 'running' | 'done' | 'failed' | 'skipped';
  result?: unknown;
  reasoning?: string;
}

export interface Plan {
  id: string;
  goal: string;
  steps: PlanStep[];
  created_at: number;
  state: 'planning' | 'executing' | 'completed' | 'failed';
}

const PLAN_TEMPLATES: Record<string, (params: Record<string, string>) => PlanStep[]> = {
  'review_and_approve': () => [
    { id: 's1', action: 'select', params: { filter: 'in_review' }, depends_on: [], state: 'pending', reasoning: 'First gather all tracks in review queue' },
    { id: 's2', action: 'introspect', params: {}, depends_on: ['s1'], state: 'pending', reasoning: 'Inspect selection to validate track quality' },
    { id: 's3', action: 'approve', params: {}, depends_on: ['s2'], state: 'pending', reasoning: 'Approve tracks that pass quality check' },
  ],
  'publish_pipeline': () => [
    { id: 's1', action: 'select', params: { filter: 'approved' }, depends_on: [], state: 'pending', reasoning: 'Target all approved tracks ready for release' },
    { id: 's2', action: 'export', params: { format: 'csv' }, depends_on: ['s1'], state: 'pending', reasoning: 'Create backup export before publishing' },
    { id: 's3', action: 'publish', params: {}, depends_on: ['s2'], state: 'pending', reasoning: 'Publish to live catalog' },
  ],
  'full_catalog_audit': () => [
    { id: 's1', action: 'status', params: { json: true }, depends_on: [], state: 'pending', reasoning: 'Get current corpus state' },
    { id: 's2', action: 'select', params: { filter: 'all' }, depends_on: ['s1'], state: 'pending', reasoning: 'Load full corpus into target set' },
    { id: 's3', action: 'export', params: { format: 'csv' }, depends_on: ['s2'], state: 'pending', reasoning: 'Export for offline analysis' },
    { id: 's4', action: 'discover', params: {}, depends_on: [], state: 'pending', reasoning: 'Check available enrichment connectors' },
  ],
  'onboard_connector': (params) => [
    { id: 's1', action: 'describe', params: { target: params.target || '' }, depends_on: [], state: 'pending', reasoning: 'Inspect connector capabilities and auth requirements' },
    { id: 's2', action: 'connect', params: { target: params.target || '' }, depends_on: ['s1'], state: 'pending', reasoning: 'Initiate connection flow' },
  ],
};

export function decomposePlan(goal: string, context: { trackCount: number; inReview: number; approved: number }): Plan {
  const goalLower = goal.toLowerCase();
  let steps: PlanStep[];

  if (goalLower.includes('review') && goalLower.includes('approv')) {
    steps = PLAN_TEMPLATES['review_and_approve']({});
  } else if (goalLower.includes('publish') || goalLower.includes('release') || goalLower.includes('go live')) {
    steps = PLAN_TEMPLATES['publish_pipeline']({});
  } else if (goalLower.includes('audit') || goalLower.includes('check everything') || goalLower.includes('full scan')) {
    steps = PLAN_TEMPLATES['full_catalog_audit']({});
  } else if (goalLower.includes('connect') || goalLower.includes('integrate') || goalLower.includes('onboard')) {
    const target = goal.replace(/connect|integrate|onboard|set up/gi, '').trim();
    steps = PLAN_TEMPLATES['onboard_connector']({ target });
  } else {
    // Intelligent decomposition: break freeform goal into generic steps
    steps = [
      { id: 's1', action: 'status', params: { json: true }, depends_on: [], state: 'pending', reasoning: 'Assess current state before acting' },
      { id: 's2', action: 'think', params: { about: goal }, depends_on: ['s1'], state: 'pending', reasoning: `Reason about: ${goal}` },
      { id: 's3', action: 'select', params: { filter: context.inReview > 0 ? 'in_review' : 'all' }, depends_on: ['s2'], state: 'pending', reasoning: 'Target relevant tracks' },
    ];
  }

  return {
    id: `plan_${crypto.randomUUID().slice(0, 8)}`,
    goal,
    steps,
    created_at: Date.now(),
    state: 'planning',
  };
}

export function describePlan(plan: Plan): string {
  const header = `Plan: ${plan.goal} [${plan.state}]`;
  const stepLines = plan.steps.map((s) => {
    const dep = s.depends_on.length > 0 ? ` (after: ${s.depends_on.join(',')})` : '';
    const status = s.state === 'done' ? '[x]' : s.state === 'running' ? '[>]' : s.state === 'failed' ? '[!]' : '[ ]';
    return `  ${status} ${s.id}: ${s.action}${dep}\n       ${s.reasoning || ''}`;
  });
  return [header, ...stepLines].join('\n');
}

// --- Function Calling Protocol ---

export interface FunctionCall {
  name: string;
  parameters: Record<string, unknown>;
  call_id: string;
}

export interface FunctionResult {
  call_id: string;
  name: string;
  result: unknown;
  error?: string;
  duration_ms: number;
}

export interface ToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, { type: string; description: string; enum?: string[] }>;
    required: string[];
  };
}

export function getToolDefinitions(): ToolDefinition[] {
  return [
    {
      name: 'catalog.select',
      description: 'Select/target tracks by status filter, title/artist match, or ID prefix',
      parameters: {
        type: 'object',
        properties: {
          filter: { type: 'string', description: 'Filter: draft, in_review, approved, published, all, or search term', enum: ['draft', 'in_review', 'approved', 'published', 'all'] },
        },
        required: ['filter'],
      },
    },
    {
      name: 'catalog.approve',
      description: 'Approve all currently targeted tracks (moves from review to approved)',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'catalog.publish',
      description: 'Publish all currently targeted tracks (moves from approved to published). Irreversible.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'catalog.review',
      description: 'Send targeted tracks back to review queue',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'catalog.export',
      description: 'Export targeted tracks as CSV or JSONL file download',
      parameters: {
        type: 'object',
        properties: {
          format: { type: 'string', description: 'Export format', enum: ['csv', 'jsonl'] },
        },
        required: [],
      },
    },
    {
      name: 'catalog.tag',
      description: 'Apply a metadata tag to all targeted tracks',
      parameters: {
        type: 'object',
        properties: {
          tag: { type: 'string', description: 'Tag name to apply' },
        },
        required: ['tag'],
      },
    },
    {
      name: 'catalog.delete',
      description: 'Permanently delete targeted tracks. Irreversible. Requires explicit consent.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'catalog.align',
      description: 'Run word-level lyric alignment on targeted tracks. Long-running async operation.',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'system.status',
      description: 'Get corpus statistics: total tracks, in_review, approved, duration',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'system.plan',
      description: 'Decompose a high-level goal into executable steps',
      parameters: {
        type: 'object',
        properties: {
          goal: { type: 'string', description: 'High-level goal to decompose' },
        },
        required: ['goal'],
      },
    },
    {
      name: 'system.memory_set',
      description: 'Store a value in working memory for later retrieval',
      parameters: {
        type: 'object',
        properties: {
          key: { type: 'string', description: 'Memory key' },
          value: { type: 'string', description: 'Value to store' },
        },
        required: ['key', 'value'],
      },
    },
    {
      name: 'system.memory_get',
      description: 'Retrieve a value from working memory',
      parameters: {
        type: 'object',
        properties: {
          key: { type: 'string', description: 'Memory key to retrieve' },
        },
        required: ['key'],
      },
    },
    {
      name: 'system.introspect',
      description: 'Get full agent state: layer, tier, targets, memory, available operations, plan state',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'connector.discover',
      description: 'List all available API connectors with their status and capabilities',
      parameters: { type: 'object', properties: {}, required: [] },
    },
    {
      name: 'connector.describe',
      description: 'Get detailed documentation for a specific connector',
      parameters: {
        type: 'object',
        properties: {
          id: { type: 'string', description: 'Connector ID or name fragment' },
        },
        required: ['id'],
      },
    },
    {
      name: 'audio.stems.separate',
      description: 'Run Demucs stem separation on targeted tracks. Extracts vocals, drums, bass, guitar, piano, other.',
      parameters: {
        type: 'object',
        properties: {
          model: { type: 'string', description: 'Separation model', enum: ['htdemucs_6s', 'htdemucs'] },
        },
        required: [],
      },
    },
    {
      name: 'audio.stems.status',
      description: 'Check stem separation job status for a track',
      parameters: {
        type: 'object',
        properties: {
          track_id: { type: 'string', description: 'Track ID to check stems for' },
        },
        required: ['track_id'],
      },
    },
  ];
}

export function parseFunctionCall(input: string): FunctionCall | null {
  // Support JSON function call syntax: { "name": "...", "parameters": {...} }
  try {
    const parsed = JSON.parse(input);
    if (parsed.name && typeof parsed.name === 'string') {
      return {
        name: parsed.name,
        parameters: parsed.parameters || {},
        call_id: `call_${crypto.randomUUID().slice(0, 8)}`,
      };
    }
  } catch { /* not JSON */ }

  // Support shorthand: func_name(param=value, param2=value2)
  const fnMatch = input.match(/^(\w+(?:\.\w+)?)\(([^)]*)\)$/);
  if (fnMatch) {
    const name = fnMatch[1];
    const paramStr = fnMatch[2];
    const params: Record<string, unknown> = {};
    if (paramStr.trim()) {
      for (const pair of paramStr.split(',')) {
        const [k, v] = pair.split('=').map(s => s.trim());
        if (k && v) params[k] = v.replace(/^["']|["']$/g, '');
      }
    }
    return { name, parameters: params, call_id: `call_${crypto.randomUUID().slice(0, 8)}` };
  }

  return null;
}

// --- Composable Pipelines ---

export interface PipelineStep {
  command: string;
  transform?: string;
}

export function parsePipeline(input: string): PipelineStep[] {
  return input.split('|').map(s => {
    const trimmed = s.trim();
    const transformMatch = trimmed.match(/^(.+?)\s*->\s*(.+)$/);
    if (transformMatch) {
      return { command: transformMatch[1].trim(), transform: transformMatch[2].trim() };
    }
    return { command: trimmed };
  });
}

// --- Introspection ---

export interface AgentIntrospection {
  agent_id: string;
  layer: string;
  tier: string;
  state: string;
  targets_count: number;
  memory_slots: number;
  active_plan: Plan | null;
  breadcrumb_depth: number;
  available_ops: string[];
  tool_definitions_count: number;
  supported_protocols: string[];
  input_modes: string[];
}

export function formatIntrospection(data: AgentIntrospection): string {
  return [
    `=== Agent Introspection ===`,
    `ID: ${data.agent_id}`,
    `Layer: ${data.layer} | Tier: ${data.tier} | State: ${data.state}`,
    `Targets: ${data.targets_count} | Memory: ${data.memory_slots} slots`,
    `Plan: ${data.active_plan ? `${data.active_plan.goal} [${data.active_plan.state}]` : 'none'}`,
    ``,
    `Available operations: ${data.available_ops.join(', ')}`,
    `Tool definitions: ${data.tool_definitions_count}`,
    ``,
    `Supported protocols: ${data.supported_protocols.join(', ')}`,
    `Input modes:`,
    `  - Natural language ("approve all tracks in review")`,
    `  - Structured command ("select review; approve")`,
    `  - Function call ({"name":"catalog.approve","parameters":{}})`,
    `  - Shorthand (catalog.select(filter=review))`,
    `  - Pipeline ("select review | approve | export")`,
    `  - Plan decomposition ("plan: review and approve everything")`,
  ].join('\n');
}
